import { useCallback, useEffect, useMemo, useState } from 'react';
import { History as HistoryIcon, MapPin, RefreshCw, User } from 'lucide-react';
import { formatDay, formatDateTime } from '@nu/shared';
import { api, ApiError } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import RouteMap from '../../components/RouteMap.jsx';

// Fleet > Vans: our vans (Contract = Network Plus in Nano Fibre's Fleet
// Master), who drives each one and the team they're in. A driver change here
// is written back to Fleet Master's Allocated Driver. "Where is it?" asks
// Samsara for the van's position there and then.

const problem = (e) => (e?.code === 'offline' ? 'No connection. Vans can only be changed online.' : e instanceof ApiError ? e.message : 'Something went wrong. Try again.');

function ago(at) {
  if (!at) return null;
  const mins = Math.round((Date.now() - new Date(at).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} days ago`;
}

export default function Vans({ me }) {
  const canEdit = me.capabilities.includes('page.vans.edit');
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [locate, setLocate] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [v, m] = await Promise.all([api('/fleet/vans'), api('/matchups')]);
      setData(v);
      setPeople(m.people.filter((p) => p.active));
      setTeams(m.teams);
    } catch (e) {
      setError(problem(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const teamOf = useMemo(() => new Map(people.map((p) => [p.id, teams.find((t) => t.id === p.teamId) || null])), [people, teams]);
  const run = async (key, fn, ok) => {
    setBusy(key);
    setNotice(null);
    try {
      const r = await fn();
      if (r?.vans) setData((d) => ({ ...d, vans: r.vans }));
      setNotice({ tone: 'ok', text: typeof ok === 'function' ? ok(r) : ok });
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    } finally {
      setBusy(null);
    }
  };

  const setDriver = (v, personId) => {
    const p = people.find((x) => x.id === personId);
    return run(`driver:${v.id}`, () => api(`/fleet/vans/${v.id}/driver`, { method: 'PUT', body: { personId: personId || null } }),
      p ? `${v.registration} is now ${p.name}'s van.` : `${v.registration} has no driver now.`);
  };
  const sync = () => run('sync', () => api('/fleet/sync', { method: 'POST' }), (r) => {
    load();
    return r.changes?.length ? `Updated from Fleet Master: ${r.changes.join('; ')}.` : 'Up to date with Fleet Master.';
  });

  const vans = (data?.vans || []).filter((v) => v.inFleet);

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Vans</h1>
            <p className="muted">Our vans from Nano Fibre&apos;s Fleet Master (Contract: Network Plus). Choose the driver here and Fleet Master is updated.</p>
          </div>
          <div className="head-actions">
            <button type="button" className="btn with-icon" onClick={() => setShowHistory(true)}><HistoryIcon size={18} aria-hidden="true" /> History</button>
            {canEdit && <button type="button" className="btn with-icon" disabled={busy !== null} onClick={sync}><RefreshCw size={18} aria-hidden="true" /> {busy === 'sync' ? 'Refreshing…' : 'Refresh'}</button>}
          </div>
        </div>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
        {data && !data.connected.fleetMaster && <p className="notice-line notice-warn">Not connected to Fleet Master on this server.</p>}
      </section>

      <section className="card">
        {error && <div className="empty"><p>{error}</p><button type="button" className="btn" onClick={load}>Try again</button></div>}
        {!error && !data && <p className="muted">Loading…</p>}
        {data && !vans.length && <p className="muted">No vans yet. A van appears here when its Contract is set to Network Plus in Fleet Master.</p>}
        {vans.length > 0 && (
          <div className="table-scroll">
            <table className="table roster vans">
              <thead><tr><th>Van</th><th>Driver</th><th>Team</th><th aria-label="Location" /></tr></thead>
              <tbody>
                {vans.map((v) => {
                  const team = v.driver ? teamOf.get(v.driver.personId) : null;
                  return (
                    <tr key={v.id}>
                      <td data-label="Van">
                        <strong>{v.registration}</strong>
                        <div className="small muted">{[v.fleetId, [v.make, v.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td data-label="Driver">
                        {canEdit
                          ? (
                            <select value={v.driver?.personId || ''} disabled={busy !== null} aria-label={`Driver of ${v.registration}`} onChange={(e) => setDriver(v, e.target.value)}>
                              <option value="">No driver</option>
                              {people.filter((p) => p.role === 'installer').map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                          )
                          : (v.driver?.name || <span className="muted">No driver</span>)}
                        {v.driver && <FmState d={v.driver} />}
                      </td>
                      <td data-label="Team">
                        {!v.driver && <span className="muted">–</span>}
                        {v.driver && !team && <span className="muted">Not in a team</span>}
                        {team && (
                          <>
                            <strong>{team.name}</strong>
                            <ul className="crew">
                              {people.filter((p) => p.teamId === team.id).map((p) => <li key={p.id}><User size={13} aria-hidden="true" />{p.name}</li>)}
                            </ul>
                          </>
                        )}
                      </td>
                      <td data-label="Location">
                        <button type="button" className="btn with-icon" onClick={() => setLocate(v)}><MapPin size={16} aria-hidden="true" /> Where is it?</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {locate && <LocateModal van={locate} onClose={() => setLocate(null)} />}
      {showHistory && <HistoryModal onClose={() => setShowHistory(false)} />}
    </div>
  );
}

function FmState({ d }) {
  if (d.fmState === 'done') return <div className="small muted">Since {formatDay(d.fromDate)} · Fleet Master updated</div>;
  if (d.fmState === 'pending') return <div className="small muted">Since {formatDay(d.fromDate)} · updating Fleet Master…</div>;
  return <div className="small warn-text" title={d.fmMessage || ''}>{d.fmState === 'not_on_site' ? 'Fleet team needs to set this driver in Fleet Master' : 'Fleet Master not updated yet; trying again'}</div>;
}

// Asks Samsara now, then shows the spot.
function LocateModal({ van, onClose }) {
  const [where, setWhere] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    api(`/fleet/vans/${van.id}/location`).then((r) => { if (live) setWhere(r); }, (e) => { if (live) setError(problem(e)); });
    return () => { live = false; };
  }, [van.id]);
  const coord = where?.lat != null ? [where.lon, where.lat] : null;
  return (
    <Modal open wide title={`Where is ${van.registration}?`} onClose={onClose} footer={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      {!where && !error && <p className="muted">Asking the tracker…</p>}
      {error && <p className="notice-line notice-bad">{error}</p>}
      {where && !coord && <p className="muted">The tracker hasn&apos;t reported a position{where.tracked ? '' : ' (this van wasn’t found in Samsara)'}.</p>}
      {coord && (
        <>
          <p><strong>{where.place || 'On the map'}</strong> <span className="muted small">· {ago(where.at)} ({formatDateTime(where.at)})</span></p>
          <RouteMap height={380} stops={[{ key: 'van', coord, label: '🚐', colour: '#0B2340', title: van.registration }]} />
          <p className="small"><a href={`https://www.google.com/maps?q=${where.lat},${where.lon}`} target="_blank" rel="noreferrer">Open in Google Maps</a></p>
        </>
      )}
    </Modal>
  );
}

function HistoryModal({ onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api('/fleet/history').then((r) => setRows(r.history), () => setRows([])); }, []);
  return (
    <Modal open wide title="Vans history" onClose={onClose} footer={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      {!rows && <p className="muted">Loading…</p>}
      {rows && !rows.length && <p className="muted">Nothing yet.</p>}
      {rows?.length > 0 && (
        <ul className="history">
          {rows.map((h) => <li key={h.id}><p>{h.summary}</p><span className="small muted">{h.actorEmail} · {formatDateTime(h.changedAt)}</span></li>)}
        </ul>
      )}
    </Modal>
  );
}
